import { Button, IconButton, IconShape, LinkButton } from '@ads/components-react';
import { CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';
import * as Dialog from '@radix-ui/react-dialog';
import { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert, FileUpload } from '@/shared/ui/atoms';
import type { FileWithProgress } from '@/shared/ui/atoms/FileUpload/types';

// ─── Tipos ────────────────────────────────────────────────────────────────────

export interface UploadPhotoModalProps {
  readonly open: boolean;
  readonly isLoading?: boolean;
  readonly isError?: boolean;
  readonly isSuccess?: boolean;
  readonly onClose: () => void;
  readonly onSubmit: (file: File) => void;
}

// ─── Componente ───────────────────────────────────────────────────────────────

/**
 * Modal para upload ou substituição da foto de perfil de uma pessoa.
 * Exibe preview da imagem selecionada antes do envio, além de estados de
 * sucesso e erro após a tentativa de upload.
 *
 * @param open - Controla visibilidade do modal
 * @param isLoading - Exibe spinner no botão de envio enquanto a mutation está pendente
 * @param isError - Exibe alerta de erro após falha no upload
 * @param isSuccess - Exibe confirmação visual após upload bem-sucedido
 * @param onClose - Callback ao fechar o modal
 * @param onSubmit - Callback com o arquivo de imagem selecionado
 */
export function UploadPhotoModal({
  open,
  isLoading = false,
  isError = false,
  isSuccess = false,
  onClose,
  onSubmit,
}: UploadPhotoModalProps) {
  const { t } = useTranslation('personManagement');
  const dropzoneId = useId();

  const [files, setFiles] = useState<FileWithProgress[]>([]);
  const [fileError, setFileError] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | undefined>(undefined);

  useEffect(() => {
    const file = files[0]?.file;
    if (!file) {
      setPreviewUrl(undefined);
      return;
    }
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [files]);

  const resetForm = () => {
    setFiles([]);
    setFileError(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = () => {
    const firstFile = files[0];
    if (!firstFile) {
      setFileError(true);
      return;
    }
    onSubmit(firstFile.file);
  };

  return (
    <Dialog.Root open={open} onOpenChange={o => !o && handleClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-none z-50 h-screen w-screen bg-base-gray-900 bg-opacity-medium backdrop-blur-soft" />

        <Dialog.Content
          className="fixed left-1/2 top-1/2 z-50 flex max-h-[85vh] w-full max-w-sm -translate-x-1/2 -translate-y-1/2 flex-col rounded-md bg-ctx-layout-surface shadow-level3 focus:outline-none"
          aria-describedby={undefined}
        >
          {/* Header */}
          <div className="flex items-center justify-between border-b-hairline border-ctx-layout-border px-xs py-mini">
            <Dialog.Title className="ts-heading-sm text-ctx-content-title">
              {t('personForm.header.photoModal.title')}
            </Dialog.Title>
            <Dialog.Close asChild>
              <IconButton
                icon={XMarkIcon}
                hierarchy="tertiary"
                size="sm"
                ariaLabel={t('personForm.header.photoModal.close')}
              />
            </Dialog.Close>
          </div>

          {/* Body */}
          <div className="flex flex-col items-center gap-xs overflow-y-auto px-xs py-mini">
            {isSuccess ? (
              <div className="flex flex-col items-center gap-xs py-sm">
                <IconShape type="success" icon={CheckIcon} size="lg" />
                <p className="ts-paragraph-sm text-center text-ctx-content-title">
                  {t('personForm.header.photoModal.successMessage')}
                </p>
              </div>
            ) : (
              <>
                {previewUrl ? (
                  <>
                    <img
                      src={previewUrl}
                      alt={t('personForm.header.photoModal.previewAlt')}
                      className="h-[8rem] w-[8rem] rounded-circular object-cover shadow-level2"
                    />
                    <label htmlFor={dropzoneId}>
                      <LinkButton
                        hierarchy="primary"
                        size="md"
                        onClick={() => document.getElementById(dropzoneId)?.click()}
                      >
                        {t('personForm.header.photoModal.changeFile')}
                      </LinkButton>
                    </label>
                    <input
                      id={dropzoneId}
                      type="file"
                      accept=".jpg,.jpeg,.png,.webp"
                      className="sr-only"
                      onChange={e => {
                        const picked = e.target.files?.[0];
                        if (picked) {
                          setFiles([{ file: picked }]);
                          setFileError(false);
                        }
                        e.target.value = '';
                      }}
                    />
                  </>
                ) : (
                  <div className="w-full">
                    <FileUpload
                      files={files}
                      addFiles={newFiles => {
                        setFiles(newFiles.map(f => ({ file: f })));
                        setFileError(false);
                      }}
                      removeFile={name => setFiles(prev => prev.filter(f => f.file.name !== name))}
                      accept=".jpg,.jpeg,.png,.webp"
                      hasError={fileError}
                      {...(fileError
                        ? {
                            helper: t('personForm.header.photoModal.fileRequired'),
                          }
                        : {})}
                    />
                  </div>
                )}

                {isError && (
                  <div className="w-full">
                    <Alert type="danger">{t('personForm.header.photoModal.errorMessage')}</Alert>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-xs border-t-hairline border-ctx-layout-border px-xs py-mini">
            {isSuccess ? (
              <Button hierarchy="secondary" size="lg" onClick={handleClose}>
                {t('personForm.header.photoModal.close')}
              </Button>
            ) : (
              <>
                <Button hierarchy="secondary" size="lg" onClick={handleClose} disabled={isLoading}>
                  {t('personForm.header.photoModal.cancel')}
                </Button>
                <Button
                  hierarchy="primary"
                  size="lg"
                  onClick={handleSubmit}
                  isLoading={isLoading}
                  disabled={!previewUrl}
                >
                  {t('personForm.header.photoModal.submit')}
                </Button>
              </>
            )}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
